import type { HourPrice } from '@greenbow/composables'

/**
 * An absolute deadline within the [today, tomorrow] scheduling window.
 * dayOffset 0 = today, 1 = tomorrow.
 */
export interface Deadline {
  dayOffset: 0 | 1
  hour: number
  /** True when the resolved deadline differs from what the user picked
   *  because tomorrow's prices aren't available yet. */
  isFallback: boolean
}

/**
 * Parses a "HH:MM" string into an absolute Deadline, choosing dayOffset
 * based on the current hour and whether tomorrow data is available.
 *
 * Rules:
 *   - If the picked hour is strictly later than `currentHour` → today.
 *   - Otherwise, if tomorrow is available → tomorrow.
 *   - Otherwise fall back to the last hour remaining today.
 */
export function parseDeadline(
  value: string,
  currentHour: number,
  tomorrowAvailable: boolean,
): Deadline {
  const [hhStr] = value.split(':')
  const hour = Number.parseInt(hhStr ?? '', 10)
  const safeHour = Number.isNaN(hour) ? 7 : Math.min(Math.max(hour, 0), 23)

  if (safeHour > currentHour) {
    return { dayOffset: 0, hour: safeHour, isFallback: false }
  }
  if (tomorrowAvailable) {
    return { dayOffset: 1, hour: safeHour, isFallback: false }
  }
  // User wanted tomorrow but data isn't available yet — fall back
  // to the last remaining hour today and flag it.
  return { dayOffset: 0, hour: 23, isFallback: true }
}

/**
 * Filters prices to those that fall inside the charging window:
 * from the current hour (inclusive) up to and including the deadline hour.
 * Past hours and post-deadline hours are dropped.
 */
export function eligibleHours(
  today: readonly HourPrice[],
  tomorrow: readonly HourPrice[] | null,
  currentHour: number,
  deadline: Deadline,
): HourPrice[] {
  const todayEligible = today.filter((p) => {
    if (p.hour < currentHour) return false
    if (deadline.dayOffset === 0 && p.hour > deadline.hour) return false
    return true
  })

  if (deadline.dayOffset === 0 || !tomorrow) {
    return todayEligible
  }

  const tomorrowEligible = tomorrow.filter((p) => p.hour <= deadline.hour)
  return [...todayEligible, ...tomorrowEligible]
}

/** Human-readable Danish label for a Deadline. */
export function formatDeadline(deadline: Deadline): string {
  const day = deadline.dayOffset === 0 ? 'I dag' : 'I morgen'
  const hh = String(deadline.hour).padStart(2, '0')
  return `${day} kl. ${hh}:00`
}
